import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, MapPin, Plane, Train, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const tabs = ['overview', 'investment', 'connectivity', 'pricing'];
const features = [
  '40 mins from Navi Mumbai International Airport',
  'Colliers-rated #1 land investment hotspot',
  '3.9X projected growth by 2030',
  'Grade-A agricultural land with clear titles',
  'Part of the 90,000+ acre NAINA smart-city corridor',
];
const drivers = ['Airport-led growth', 'Global tech investment', 'NAINA Smart City', 'Mega connectivity', 'Tourism demand', 'Mumbai-Pune midpoint'];
const connectivity = [
  ['NMIA', '40 mins', Plane], ['South Mumbai', '80 mins', Train], ['Pune', '90 mins', Train], ['Lonavala', '20 mins', MapPin], ['Imagicaa', '7 mins', MapPin],
];

const Projects = () => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <section id="projects" className="relative overflow-hidden bg-[#F5F5F5] py-24 sm:py-32" data-testid="featured-project-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-5 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Mandate 01</div>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-[#0B1F3B] sm:text-4xl">Kewat’s Legacy — where Mumbai 3.0 meets open land.</h2>
          </div>
          <Button asChild className="h-12 rounded-full bg-[#0B1F3B] px-6 text-white hover:bg-[#06101E]" data-testid="featured-project-details-btn">
            <Link to="/projects/kewats-legacy">Open full mandate <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.75 }} className="group relative min-h-[34rem] overflow-hidden rounded-[2rem] bg-[#0B1F3B] shadow-[0_35px_100px_rgba(6,16,30,0.22)]">
            <img src="https://images.unsplash.com/photo-1705897141281-88daf3380a3a" alt="Kewat's Legacy hillside estate" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06101E]/90 via-[#06101E]/20 to-transparent" />
            <div className="absolute left-6 top-6 rounded-full bg-[#F5F5F5]/90 px-4 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B] backdrop-blur-md">Khopoli · MMR</div>
            <div className="absolute bottom-6 left-6 right-6 rounded-3xl border border-white/10 bg-[#06101E]/60 p-6 text-white backdrop-blur-xl">
              <div className="mb-3 flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#C5A880]"><TrendingUp className="h-4 w-4" /> ₹65 lakhs introductory</div>
              <div className="font-display text-3xl font-semibold">20,000 sq.ft Grade-A plots</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.75, delay: 0.08 }} className="rounded-[2rem] border border-[#0B1F3B]/10 bg-white p-5 shadow-[0_25px_80px_rgba(6,16,30,0.09)] sm:p-7">
            <div className="mb-6 grid grid-cols-2 gap-2 rounded-2xl bg-[#F5F5F5] p-2 sm:grid-cols-4" data-testid="project-tab-list">
              {tabs.map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`project-tab-${tab}`} className={`rounded-xl px-3 py-3 font-tech text-[10px] uppercase tracking-[0.14em] transition-colors ${activeTab === tab ? 'bg-[#0B1F3B] text-white' : 'text-[#0B1F3B]/56 hover:bg-white hover:text-[#0B1F3B]'}`}>
                  {tab}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div data-testid="project-tab-panel-overview">
                <img src="https://customer-assets.emergentagent.com/job_plotted-homes-india/artifacts/49unplui_ChatGPT%20Image%20Apr%208%2C%202026%2C%2004_58_03%20PM%20%281%29.png" alt="Kewat's Legacy project overview" className="mb-6 h-56 w-full rounded-3xl object-cover" />
                <div className="grid gap-3">{features.map((feature) => <div key={feature} className="flex gap-3 text-sm text-[#0B1F3B]/70"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#1E7B54]" />{feature}</div>)}</div>
              </div>
            )}
            {activeTab === 'investment' && (
              <div className="grid gap-3 sm:grid-cols-2" data-testid="project-tab-panel-investment">{drivers.map((driver, index) => <div key={driver} className="rounded-2xl border border-[#0B1F3B]/10 bg-[#F5F5F5] p-5"><div className="font-tech text-[10px] text-[#C5A880]">0{index + 1}</div><div className="mt-3 font-display text-xl font-semibold">{driver}</div></div>)}</div>
            )}
            {activeTab === 'connectivity' && (
              <div className="grid gap-3" data-testid="project-tab-panel-connectivity">{connectivity.map(([place, time, Icon]) => <div key={place} className="flex items-center justify-between rounded-2xl border border-[#0B1F3B]/10 bg-[#F5F5F5] p-5"><span className="flex items-center gap-3 font-semibold"><Icon className="h-5 w-5 text-[#C5A880]" />{place}</span><span className="font-tech text-xs text-[#0B1F3B]/60">{time}</span></div>)}</div>
            )}
            {activeTab === 'pricing' && (
              <div className="rounded-3xl bg-[#0B1F3B] p-7 text-white" data-testid="project-tab-panel-pricing">
                <div className="font-tech text-[10px] uppercase tracking-[0.22em] text-[#C5A880]">Introductory offer</div>
                <div className="mt-4 font-display text-5xl font-semibold">₹65L*</div>
                <div className="mt-2 text-white/60">for 20,000 sq.ft · previously ₹70L</div>
                <div className="mt-8 border-t border-white/10 pt-5 text-sm leading-relaxed text-white/60">Limited inventory. Taxes and registration additional. Site visits coordinated directly by Next Leap Inc.</div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
